import {
	ControlledCheckbox,
	ControlledInput,
	ControlledRadioGroup,
} from "@dune/ui";
import { ClientSociety } from "@dune/types";
import { isAxiosError } from "axios";
import { useCallback, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetSocietyInfoFromSirenNumber } from "../../../api/queries/siren.queries";
import {
	formatClientSocietyForForm,
	updateFormSirenMOASociety,
} from "../../../helpers/newClientForm.helper";
import IsSyndicContainer from "./IsSyndicContainer";
import NewClientUserForm from "./NewClientUserForm";
import SirenSocietyForm from "./SirenClientForm";
import { CreateConstructionSiteClientFormData } from "./useCreateConstructionSiteForm";

export type NewClientFormProps = {
	isMOAClient: boolean;
	excludeSyndics?: boolean;
};

export default function NewClientForm({
	isMOAClient,
	excludeSyndics = false,
}: NewClientFormProps) {
	const { control, formState, setValue, clearErrors, getValues } =
		useFormContext<CreateConstructionSiteClientFormData>();
	const { errors } = formState;
	const clientTypeValue = useWatch({
		control,
		name: isMOAClient ? "moaClientType" : "clientType",
	});
	const clientSocietyIsSyndic = useWatch({
		control,
		name: "clientSociety.isSyndic",
	});

	const moaClientSocietySirenValue = useWatch({
		control,
		name: "moaClientSociety.siren",
	});

	const {
		data: moaClientSocietyData,
		isSuccess: isMOASocietySirenSuccess,
		isError: isMOASocietySirenError,
		error: moaSocietySirenError,
	} = useGetSocietyInfoFromSirenNumber(moaClientSocietySirenValue);

	const handleExistingSocietySelection = useCallback(
		(existingSociety: ClientSociety) => {
			const currentSelectedId = getValues("clientSocietyId");
			if (currentSelectedId === existingSociety.id) {
				return;
			}
			setValue("clientType", "SOCIETY");
			setValue("clientSocietyId", existingSociety.id);
			setValue("clientSociety", formatClientSocietyForForm(existingSociety));
		},
		[getValues, setValue],
	);

	const moaSocietySirenAxiosError =
		isMOASocietySirenError && isAxiosError(moaSocietySirenError)
			? moaSocietySirenError.response?.data
			: null;

	useEffect(() => {
		if (moaClientSocietySirenValue === null) return;
		updateFormSirenMOASociety(
			isMOASocietySirenSuccess,
			moaClientSocietySirenValue,
			moaClientSocietyData,
			setValue,
		);
		clearErrors(["moaClientSociety"]);
	}, [
		moaClientSocietyData,
		isMOASocietySirenSuccess,
		setValue,
		clearErrors,
		moaClientSocietySirenValue,
	]);

	return (
		<div className="flex flex-col gap-4">
			<h1 className="text-[#144C65] text-2xl font-medium">
				{isMOAClient ? "MOA" : "Nouveau client"}
			</h1>
			<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4">
				<ControlledRadioGroup
					control={control}
					defaultValue="SOCIETY"
					name={isMOAClient ? "moaClientType" : "clientType"}
					label="Personne physique ou morale ?"
					items={[
						{
							id: isMOAClient ? "moa-user-radio" : "user-radio",
							value: "USER",
							label: "Physique",
						},
						{
							id: isMOAClient ? "moa-society-radio" : "society-radio",
							value: "SOCIETY",
							label: "Morale",
						},
					]}
				/>
			</div>
			<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4 gap-4">
				{clientTypeValue === "USER" ? (
					<div className="flex flex-col items-center gap-4">
						{isMOAClient ? (
							<div className="flex flex-col gap-4 w-full">
								<ControlledInput
									control={control}
									name={"moaClientUser.lastname"}
									label="Nom"
									error={
										"moaClientUser" in errors
											? errors.moaClientUser?.firstname?.message?.toString()
											: undefined
									}
								/>
								<ControlledInput
									control={control}
									name={"moaClientUser.firstname"}
									label="Prénom"
									error={
										"moaClientUser" in errors
											? errors.moaClientUser?.firstname?.message?.toString()
											: undefined
									}
								/>
							</div>
						) : (
							<NewClientUserForm />
						)}
					</div>
				) : (
					<div className="flex flex-col justify-start items-center gap-4">
						{isMOAClient ? (
							<>
								<ControlledInput
									control={control}
									name={"moaClientSociety.siren"}
									label="SIREN/SIRET"
									maxLength={14}
									error={
										moaSocietySirenAxiosError
											? moaSocietySirenAxiosError.message
											: "moaClientSociety" in errors
												? errors.moaClientSociety?.siren?.message?.toString()
												: undefined
									}
								/>
								<ControlledInput
									control={control}
									name={"moaClientSociety.denomination"}
									label="Raison sociale"
									error={
										"moaClientSociety" in errors
											? errors.moaClientSociety?.denomination?.message?.toString()
											: undefined
									}
									disabled={!!moaClientSocietyData || moaSocietySirenAxiosError}
								/>

								<div className="flex flex-row gap-4 w-full">
									<ControlledInput
										control={control}
										name={"moaClientSociety.streetNumber"}
										label="Numéro de rue"
										error={
											"moaClientSociety" in errors
												? errors.moaClientSociety?.streetNumber?.message?.toString()
												: undefined
										}
										disabled={
											!!moaClientSocietyData || moaSocietySirenAxiosError
										}
									/>
									<ControlledInput
										control={control}
										name={"moaClientSociety.streetName"}
										label="Nom de rue"
										error={
											"moaClientSociety" in errors
												? errors.moaClientSociety?.streetNumber?.message?.toString()
												: undefined
										}
										disabled={
											!!moaClientSocietyData || moaSocietySirenAxiosError
										}
									/>
								</div>

								<div className="flex flex-row gap-4 w-full">
									<ControlledInput
										control={control}
										name={"moaClientSociety.postalCode"}
										label="Code postal"
										error={
											"moaClientSociety" in errors
												? errors.moaClientSociety?.streetNumber?.message?.toString()
												: undefined
										}
										disabled={
											!!moaClientSocietyData || moaSocietySirenAxiosError
										}
									/>
									<ControlledInput
										control={control}
										name={"moaClientSociety.city"}
										label="Ville"
										error={
											"moaClientSociety" in errors
												? errors.moaClientSociety?.streetNumber?.message?.toString()
												: undefined
										}
										disabled={
											!!moaClientSocietyData || moaSocietySirenAxiosError
										}
									/>
								</div>
							</>
						) : (
							<>
								<SirenSocietyForm
									formKey="clientSociety"
									onExistingSocietyDetected={
										isMOAClient ? undefined : handleExistingSocietySelection
									}
									allowManualAddress={!!clientSocietyIsSyndic}
								/>
								{!excludeSyndics && (
									<ControlledCheckbox
										control={control}
										id="clientSociety.isSyndic"
										name="clientSociety.isSyndic"
										label="Il s'agit d'un syndic de copropriété"
									/>
								)}
								{!!clientSocietyIsSyndic && (
									<IsSyndicContainer formKey="clientSociety" />
								)}
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
